import React, { useEffect, useRef, useState } from 'react';
import { Modal, View, TouchableOpacity, NativeTouchEvent, GestureResponderEvent } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Svg, { Circle, Polyline } from 'react-native-svg';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import VideoPlayer from '../../../components/VideoPlayer';
import TimelineWrapper from '../../../components/TimelineWrapper';
import { cameraService } from '../../../services/central-api/cameras';
import { centralAPI } from '../../../services/central-api';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Mustache from 'mustache';
import { ScrollView } from 'react-native-web-hover';
import Tooltip from '../../../components/Tooltip';
import { useAuth } from '../../../context/Auth';
import { crmService } from '../../../services/central-api/crm';
import { WindowInformation } from '../../../services/window-information';
import CustomButton from '../../../components/CustomButton';
import { VideoPlayerControls } from '../../../components/VideoPlayerControls';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';


type TouchableOpacityWithNativeNode = TouchableOpacity & {
    getInnerViewNode?: () => HTMLElement;
};

interface AreaInvasionModalProps {
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    cameraId?: string;
    onSuccess?: () => void;
}

export default function AreaInvasionModal({
    isModalVisible,
    setIsModalVisible,
    cameraId,
}: AreaInvasionModalProps) {
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [cameraData, setCameraData] = useState<Camera | undefined>();
    const [maxPoints, setMaxPoints] = useState<number>(10);
    const minPoints = 3;
    const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });

    const [polygonPoints, setPolygonPoints] = useState<{ x: number; y: number; }[]>([]);
    const [isPolygonClosed, setIsPolygonClosed] = useState(false);
    const [cursor, setCursor] = useState<number>(Date.now());
    const [isLive, setIsLive] = useState(true);
    const [currentRecord, setCurrentRecord] = useState<CameraRecord | undefined>();
    const [currentTempStreamMoment, setCurrentTempStreamMoment] = useState<number>();
    const [modalVideoPlayer, setModalVideoPlayer] = useState<VideoPlayer | null>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [userBearerToken, setUserBearerToken] = useState<string>();

    const svgContainerRef = useRef<View>(null);
    const touchableRef = useRef<TouchableOpacityWithNativeNode>(null);
    const lastSync = cameraData?.cameraMetadata?.configurationLastSynchronization?.data?.areaInvasion?.finishedAt;



    const toPixel = (p: { x: number; y: number; }) => ({
        x: p.x * containerSize.width,
        y: p.y * containerSize.height,
    });

    useEffect(() => {
        (async () => {
            const token = await centralAPI.getToken();
            if (token) {
                setUserBearerToken(token);
            }
        })();
    }, []);

    useEffect(() => {
        const element = touchableRef.current;
        if (!element) {
            return;
        }
        const node = element.getInnerViewNode ? element.getInnerViewNode() : (element as unknown as HTMLElement);

        const handleContextMenu = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (polygonPoints.length >= minPoints && !isPolygonClosed) {
                setIsPolygonClosed(true);
            }
        };

        node?.addEventListener('contextmenu', handleContextMenu);
        return () => node?.removeEventListener('contextmenu', handleContextMenu);
    }, [polygonPoints, isPolygonClosed]);

    useEffect(() => {
        if (!modalVideoPlayer || !cameraData || !isModalVisible) {
            return;
        }

        loadLiveStream(cameraData, modalVideoPlayer);

        return () => {
            modalVideoPlayer.componentWillUnmount();
        };
    }, [modalVideoPlayer, cameraData, isModalVisible]);

    useEffect(() => {
        if (!isModalVisible || !cameraId) {
            return;
        }

        fetchCameraData(cameraId);
        setIsLive(true);
        setCurrentRecord(undefined);
        setCursor(Date.now());
        setIsPlaying(true);
    }, [isModalVisible, cameraId]);

    const isEnabled = isModalVisible && !!authenticatedUser && !!cameraId;


    const { data: recordList } = useQuery({
        queryKey: ['areaInvasionTimeline', cameraId],
        queryFn: async () => await cameraService.getTimeline(cameraId!),
        enabled: isEnabled,
        refetchIntervalInBackground: true,
        refetchInterval: (data, query) => {
            if (query.state.status === 'error') {
                return 5000;
            }
            return 60000;
        },
    });

    useEffect(() => {
        if (!isModalVisible || !cameraData?.cameraConfiguration) {
            return;
        }

        if (cameraData.cameraConfiguration.invasionAreaCoordinates?.length) {
            setPolygonPoints(cameraData.cameraConfiguration.invasionAreaCoordinates);
        } else {
            setPolygonPoints([]);
        }
        setIsPolygonClosed(true);
    }, [isModalVisible, cameraData]);

    useEffect(() => {
        if (!isModalVisible) {
            return;
        }
        const interval = setInterval(() => {
            if (isLive) {
                setCursor(new Date().getTime());
            } else {
                if (modalVideoPlayer && currentRecord) {
                    const time = modalVideoPlayer.getTime();
                    const initialTime = currentTempStreamMoment ?? currentRecord.start;
                    // TODO: calcular milisegundos usando o moment
                    const date = new Date((time * 1000) + initialTime);
                    setCursor(date.getTime());
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [modalVideoPlayer, currentRecord, isLive, isModalVisible, currentTempStreamMoment]);

    useEffect(() => {
        if (!modalVideoPlayer) {
            return;
        }
        if (isLive) {
            setIsPlaying(true);
        }
    }, [modalVideoPlayer, isLive]);

    useEffect(() => {
        if (!modalVideoPlayer) {
            return;
        }
        modalVideoPlayer.setPlaying(isPlaying);
    }, [modalVideoPlayer, isPlaying]);

    async function fetchCameraData(id: string) {
        try {
            const camera = await crmService.getCamera(id);
            setCameraData(camera);
            setMaxPoints(camera.manufacturer === 'hikvision' ? 20 : 10);
        } catch (err) {
            console.error(err);
        }
    }

    async function setTimeLineAt(moment: number) {
        if (!modalVideoPlayer) {
            return;
        }
        setIsLive(false);

        if (!recordList) {
            return;
        }

        const cameraRecord = recordList.videos.findLast((record: CameraRecord) => {
            return record.finish > moment && record.start <= moment;
        });

        if (!cameraRecord || !cameraData) {
            return;
        }

        setCursor(moment);

        if (cameraRecord !== currentRecord || cameraData.storageType === 'card') {
            setCurrentRecord(cameraRecord);
            if (cameraData.storageType === 'card') {
                setCurrentTempStreamMoment(moment);
                modalVideoPlayer.setSrc({
                    srcObject: {
                        type: 'flv',
                        tempStream: true,
                        videoDuration: cameraRecord.finish - moment,
                        getTempFlvSrc: async () => {
                            const recordedRtsp = await cameraService.cardCameraFlvStream(cameraData!.id, moment);
                            return { src: recordedRtsp.flvUrl };
                        },
                        onVideoEnded: async () => {
                            try {
                                const index = recordList.videos.indexOf(cameraRecord) + 1;

                                if (index < recordList.videos.length) {
                                    await setTimeLineAt(recordList.videos[index].start);
                                } else {

                                    setCursor(new Date().getTime());
                                    loadLiveStream(cameraData, modalVideoPlayer);
                                    setCurrentRecord(undefined);
                                    setIsLive(true);
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        }
                    },
                });
                return;
            }

            let videoURL;
            if (typeof recordList.videoTemplate === 'string') {
                videoURL = Mustache.render(recordList.videoTemplate, cameraRecord);
            } else if (recordList.videoTemplate && cameraRecord.videoStorageProviderId) {
                videoURL = Mustache.render(recordList.videoTemplate[cameraRecord.videoStorageProviderId], cameraRecord);
            }
            if (cameraRecord.videoStorageProviderId && (!cameraData.thirdPartyCamera || (cameraData.thirdPartyCamera && cameraData.originalCamera))) {
                const tokenResponse = await cameraService.getTimelineVideoSasToken(
                    cameraData.id,
                    cameraRecord.start,
                    cameraRecord.finish,
                    cameraRecord.videoStorageProviderId,
                );
                videoURL += `?${tokenResponse.sasToken}`;
            }

            if (!videoURL) {
                return;
            }

            const srcUrl = new URL(videoURL);
            const newSrc = srcUrl.origin + srcUrl.pathname;
            let isMP4Video = false;
            try {
                isMP4Video = newSrc.endsWith('.mp4');
            } catch (error) {
                console.error('invalid URL for video source:', videoURL);
            }

            modalVideoPlayer.setSrc({
                srcObject: {
                    type: isMP4Video ? 'mp4' : 'hls',
                    src: videoURL,
                    onVideoEnded: async () => {
                        try {
                            const index = recordList.videos.indexOf(cameraRecord) + 1;

                            if (index < recordList.videos.length) {
                                await setTimeLineAt(recordList.videos[index].start);
                            } else {

                                setCursor(new Date().getTime());
                                loadLiveStream(cameraData, modalVideoPlayer);
                                setCurrentRecord(undefined);
                                setIsLive(true);
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }
                }
            });
        }

        modalVideoPlayer.setTimeAt((moment - cameraRecord.start) / 1000);
    }

    function onRecordClick(moment: number) {
        setTimeLineAt(moment);
    }

    function loadLiveStream(camera: Camera, videoPlayer: VideoPlayer) {

        if (camera.storageType === 'card') {
            return videoPlayer.setSrc({
                srcObject: {
                    type: 'flv',
                    tempStream: true,
                    getTempFlvSrc: async () => {
                        const live = await cameraService.cardCameraFlvStream(camera!.id);
                        return { src: live.flvUrl };
                    },
                },
            });
        }

        if (!camera.streamUrl) {
            return;
        }

        if (camera.thirdPartyCamera?.type === 'hls') {
            return videoPlayer.setSrc({
                srcObject: {
                    type: 'hls',
                    src: camera.streamUrl,
                }
            });
        }

        videoPlayer.setSrc({
            srcObject: {
                type: 'flv',
                tempStream: false,
                src: camera.streamUrl,
            },
        });
    }

    async function handleGoLive() {
        if (!modalVideoPlayer) {
            return;
        }

        if (isLive) {
            return;
        }

        if (!cameraData) {
            return;
        }

        setCursor(new Date().getTime());
        loadLiveStream(cameraData, modalVideoPlayer);
        setCurrentRecord(undefined);
        setIsLive(true);
    }

    function resetDrawing() {
        setPolygonPoints([]);
        setIsPolygonClosed(false);
    }

    function handleClose() {
        setIsModalVisible(false);
        resetDrawing();
        setCameraData(undefined);
    }

    function handleAddPoint(event: GestureResponderEvent) {
        if (isPolygonClosed) {
            return;
        }

        if (polygonPoints.length >= maxPoints) {
            Toast.show({
                type: 'error',
                text1: `Limite de ${maxPoints} pontos atingido`,
            });
            return;
        }

        const touch = event.nativeEvent as NativeTouchEvent;
        if (svgContainerRef.current) {
            svgContainerRef.current.measure((_x, _y, _width, _height, pageX, pageY) => {
                const relativeX = touch.pageX - pageX;
                const relativeY = touch.pageY - pageY;
                setPolygonPoints((prev) => [...prev, {
                    x: relativeX / containerSize.width,
                    y: relativeY / containerSize.height,
                }]);
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={handleClose}
        >
            <View style={styles.overlay}>
                <View style={styles.container(windowInfo.isMobile)}>

                    <View style={styles.header}>
                        <MyAppText style={styles.headerTitle}>
                            {translate('areaInvasionDrawing')}
                        </MyAppText>
                        <TouchableOpacity onPress={handleClose}>
                            <FontAwesomeIcon icon={faTimes} color={theme.colors.iconColor} fontSize={18} />
                        </TouchableOpacity>
                    </View>

                    <ScrollView style={styles.body} contentContainerStyle={{ gap: 24 }}>
                        <View style={styles.descriptionRow}>
                            <MyAppText style={styles.bodyDescription}>
                                {translate('createOrEditAreaInvasionDrawing')}
                            </MyAppText>
                            {lastSync && (
                                <MyAppText style={styles.syncInfoText}>
                                    {translate('lastSynchronization')}: {moment(lastSync).format('DD/MM/YYYY HH:mm')}
                                </MyAppText>
                            )}
                        </View>
                        <View>
                            <View
                                ref={svgContainerRef}
                                style={styles.drawingArea}
                                onLayout={(e) => {
                                    const { width, height } = e.nativeEvent.layout;
                                    setContainerSize({ width, height });
                                }}
                            >

                                {cameraData?.streamUrl ? (
                                    <View style={styles.videoPlayerWrapper} pointerEvents="none">
                                        {userBearerToken &&
                                            <VideoPlayer
                                                camera={cameraData}
                                                watermark={String(authenticatedUser?.id)}
                                                ref={(player) => setModalVideoPlayer(player)}
                                                onClickEvent={() => { }}
                                                enableCameraInfo={false}
                                                enableZoom={false}
                                                userBearerToken={userBearerToken}
                                            />
                                        }
                                    </View>
                                ) : (
                                    <View style={[styles.videoPlayerWrapper, { backgroundColor: '#000', justifyContent: 'center', alignItems: 'center' }]}>
                                        <MyAppText style={{ color: '#fff' }}>{translate('loadingCamera')}</MyAppText>
                                    </View>
                                )}

                                <Svg
                                    height="100%"
                                    width="100%"
                                    style={styles.svg}
                                    pointerEvents="none"
                                >
                                    {polygonPoints.length >= 2 && (
                                        <>
                                            <Polyline
                                                points={[
                                                    ...polygonPoints,
                                                    ...(isPolygonClosed ? [polygonPoints[0]] : []),
                                                ]
                                                    .map((p) => {
                                                        const px = toPixel(p); return `${px.x},${px.y}`;
                                                    })
                                                    .join(' ')}
                                                fill="transparent"
                                                stroke="black"
                                                strokeWidth="4"
                                            />
                                            <Polyline
                                                points={[
                                                    ...polygonPoints,
                                                    ...(isPolygonClosed ? [polygonPoints[0]] : []),
                                                ]
                                                    .map((p) => {
                                                        const px = toPixel(p); return `${px.x},${px.y}`;
                                                    })
                                                    .join(' ')}
                                                fill='transparent'
                                                stroke="yellow"
                                                strokeWidth="2"
                                                strokeDasharray="5"
                                            />
                                        </>
                                    )}

                                    {polygonPoints.map((p, idx) => (
                                        <React.Fragment key={idx}>
                                            <Circle cx={toPixel(p).x} cy={toPixel(p).y} r="5" fill="black" />
                                            <Circle cx={toPixel(p).x} cy={toPixel(p).y} r="3" fill="yellow" />
                                        </React.Fragment>
                                    ))}
                                </Svg>

                                <TouchableOpacity
                                    ref={touchableRef}
                                    activeOpacity={1}
                                    onPress={handleAddPoint}
                                    style={styles.clickArea}
                                />

                                <View style={styles.toolbar} pointerEvents="box-none">
                                    <Tooltip
                                        style={{ zIndex: 20 }}
                                        tooltipDirection='right'
                                        tooltipContent={
                                            <View style={{ paddingVertical: 5, paddingHorizontal: 8 }}>
                                                <MyAppText style={{ color: theme.colors.badges.transparencyBlack.textColor }}>
                                                    {translate('polygonPointsInfo')}
                                                </MyAppText>
                                            </View>
                                        }
                                    >
                                        <View style={styles.toolbarBadge} pointerEvents="none">
                                            <MyAppText style={styles.toolbarBadgeText}>
                                                {isPolygonClosed
                                                    ? `${translate('points')}: ${polygonPoints.length}/${maxPoints} ✓`
                                                    : `${translate('points')}: ${polygonPoints.length}/${maxPoints}`}
                                            </MyAppText>
                                        </View>
                                    </Tooltip>

                                </View>
                            </View>

                            {modalVideoPlayer && (
                                <VideoPlayerControls
                                    isLive={isLive}
                                    isPlaying={isPlaying}
                                    isTheaterMode={false}
                                    handleGoLive={handleGoLive}
                                    onStepBack={() => { }}
                                    onStepForward={() => { }}
                                    onTogglePlay={() => setIsPlaying(!isPlaying)}
                                    onPressTheaterMode={() => { }}
                                    onPressFullscreen={() => { }}
                                    onPlaybackChange={() => { }}
                                    hideFrameByFrame
                                    hideNavigationControls
                                    hideTheaterMode
                                    hideFullscreen
                                />
                            )}

                            {cursor && cameraId && (
                                <TimelineWrapper
                                    cursor={cursor}
                                    recordList={recordList ?? { videos: [] }}
                                    onRecordClick={onRecordClick}
                                    isLoading={!recordList}
                                />
                            )}
                        </View>
                    </ScrollView>

                    <View style={styles.footer}>
                        <CustomButton
                            onPress={() => setIsModalVisible(false)}
                            text={translate('cancel')}
                            type='secondary'
                        />
                    </View>

                </View>
            </View >
        </Modal >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    overlay: {
        flex: 1,
        backgroundColor: theme.colors.badges.transparencyBlack.backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: (isMobile: boolean) => {
        return {
            width: isMobile ? '95%' : '55%',
            maxHeight: '95%',
            borderRadius: 12,
            overflow: 'hidden',
            backgroundColor: theme.colors.backgrounds['BG-1'],
            flexDirection: 'column',
            padding: 24,
            gap: 10
        };
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: theme.colors.neutral[500],
    },
    body: {
        flex: 1,
    },
    bodyDescription: {
        fontSize: 14,
        color: theme.colors.neutral[400],
    },
    drawingArea: {
        width: '100%',
        aspectRatio: 16 / 9,
        overflow: 'hidden',
        position: 'relative',
    },
    videoPlayerWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
    },
    svg: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 5,
    },
    clickArea: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 10,
    },
    toolbar: {
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 15,
        flexDirection: 'column',
        rowGap: 8,
        alignItems: 'flex-start',
    },
    toolbarBadge: {
        minWidth: 100,
        height: 40,
        backgroundColor: theme.colors.badges.transparencyBlack.backgroundColor,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: theme.colors.white[500],
        paddingHorizontal: 8,
    },
    toolbarBadgeText: {
        fontSize: 12,
        color: theme.colors.badges.transparencyBlack.textColor,
    },
    toolbarButton: {
        width: 40,
        height: 40,
        backgroundColor: theme.colors.badges.transparencyBlack.backgroundColor,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: theme.colors.white[500],
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },
    descriptionRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    syncInfoText: {
        fontSize: 12,
        color: theme.colors.neutral[400],
    },
}));
